import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/use-toast';
import { cn } from '@shared/utils/utils';
import { useImportAdSpend } from '../../hooks';
import type { ProfitabilityAdSpendImportResponse, ProfitabilityAdSpendImportSummary } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { getProfitabilityAdImportErrorMessage } from '../../utils/profitabilityApiError';

type Step = 'file' | 'result';

export interface AdSpendImportModalRef {
  open: () => void;
  close: () => void;
}

interface AdSpendImportModalProps {
  onSuccess?: () => void;
}

export const AdSpendImportModal = forwardRef<AdSpendImportModalRef, AdSpendImportModalProps>(
  ({ onSuccess }, ref) => {
    const { t, i18n } = useTranslation('profitabilityModule');
    const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';
    const currency = useCurrency();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const modalRef = useRef<ModalCenterRef>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>('file');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<ProfitabilityAdSpendImportResponse | null>(null);

    const importM = useImportAdSpend();

    const resetState = () => {
      setStep('file');
      setSelectedFile(null);
      setDragActive(false);
      setError(null);
      setImportResult(null);
      if (inputRef.current) inputRef.current.value = '';
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        resetState();
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const validateAndSetFile = (file?: File) => {
      if (!file) return;
      if (file.size === 0) {
        setError(t('ad_spend.validation_file_empty'));
        return;
      }
      const lower = file.name.toLowerCase();
      if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
        setError(t('ad_spend.validation_file_type'));
        return;
      }
      setSelectedFile(file);
      setError(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      validateAndSetFile(e.target.files?.[0]);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (importM.isPending) return;
      validateAndSetFile(e.dataTransfer.files?.[0]);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
    };

    const handleImport = async () => {
      if (!selectedFile) {
        setError(t('ad_spend.import_modal.no_file'));
        return;
      }
      setError(null);
      try {
        const raw = await importM.mutateAsync(selectedFile);
        const emptySummary: ProfitabilityAdSpendImportSummary = {
          rows_read: 0,
          rows_skipped: 0,
          campaigns_created: 0,
          spends_created: 0,
          spends_updated: 0,
          spends_unchanged: 0,
        };
        const result: ProfitabilityAdSpendImportResponse = {
          summary: raw.summary ?? emptySummary,
          changes: {
            campaigns_created: raw.changes?.campaigns_created ?? [],
            spends_created: raw.changes?.spends_created ?? [],
            spends_updated: raw.changes?.spends_updated ?? [],
            errors: raw.changes?.errors ?? [],
          },
          truncated: raw.truncated,
          message: raw.message,
        };
        setImportResult(result);
        setStep('result');
        toast({
          title: t('ad_spend.toast.import_ok'),
          description: t('ad_spend.import_ok_short'),
        });
        await queryClient.invalidateQueries({ queryKey: ['profitability', 'ad-spend'] });
        onSuccess?.();
      } catch (err) {
        const message = getProfitabilityAdImportErrorMessage(
          err,
          t('errors.generic'),
          t('ad_spend.error_import_corrupt_or_empty'),
        );
        setError(message);
        toast({
          title: t('ad_spend.toast.error'),
          description: message,
          variant: 'destructive',
        });
      }
    };

    const handleClose = () => {
      if (!importM.isPending) modalRef.current?.closeModal();
    };

    const dropzoneClasses = cn(
      'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-8 transition-colors',
      dragActive ? 'border-primary bg-muted/40' : 'border-border bg-background',
      importM.isPending ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    );

    const s = importResult?.summary;
    const ch = importResult?.changes;
    const campaignsCreated = ch?.campaigns_created ?? [];
    const spendsCreated = ch?.spends_created ?? [];
    const spendsUpdated = ch?.spends_updated ?? [];
    const errList = ch?.errors ?? [];

    return (
      <ModalCenter
        ref={modalRef}
        title={t('ad_spend.import_modal.title')}
        width="xxl"
        type="default"
        className="border-border bg-background"
        data-testid="profitability-ad-spend-import-modal"
      >
        <div className="space-y-6">
          {step === 'file' && (
            <>
              <p className="text-sm text-muted-foreground">{t('ad_spend.import_hint')}</p>
              <div
                className={dropzoneClasses}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !importM.isPending && inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="hidden"
                  onChange={handleInputChange}
                  data-testid="profitability-ad-spend-file-input"
                />
                <Upload className={cn('h-10 w-10', selectedFile ? 'text-primary' : 'text-muted-foreground')} />
                <p className="text-center text-sm text-muted-foreground">
                  {selectedFile ? (
                    <span className="font-medium text-foreground">{selectedFile.name}</span>
                  ) : (
                    t('ad_spend.import_modal.dropzone_prompt')
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{t('ad_spend.import_modal.format_hint')}</p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" className="border-border" onClick={handleClose} disabled={importM.isPending}>
                  {t('ad_spend.cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={handleImport}
                  disabled={!selectedFile || importM.isPending}
                  data-testid="profitability-ad-spend-import-submit"
                >
                  {importM.isPending ? t('ad_spend.importing') : t('ad_spend.import_modal.import_action')}
                </Button>
              </div>
            </>
          )}

          {step === 'result' && importResult && s && ch && (
            <>
              {importResult.truncated && (
                <Alert className="border-border bg-muted/30">
                  <AlertTitle className="text-foreground">{t('ad_spend.import_modal.truncated_title')}</AlertTitle>
                  <AlertDescription className="text-muted-foreground">{t('ad_spend.import_truncated')}</AlertDescription>
                </Alert>
              )}

              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <h4 className="mb-3 text-sm font-semibold text-foreground">{t('ad_spend.import_modal.summary_title')}</h4>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground">
                  <span>
                    {t('ad_spend.import_modal.summary_rows_read')}: <strong className="tabular-nums">{s.rows_read}</strong>
                  </span>
                  <span>
                    {t('ad_spend.import_modal.summary_rows_skipped')}: <strong className="tabular-nums">{s.rows_skipped}</strong>
                  </span>
                  <span>
                    {t('ad_spend.import_modal.summary_campaigns_created')}:{' '}
                    <strong className="tabular-nums">{s.campaigns_created}</strong>
                  </span>
                  <span>
                    {t('ad_spend.import_modal.summary_spends_created')}:{' '}
                    <strong className="tabular-nums">{s.spends_created}</strong>
                  </span>
                  <span>
                    {t('ad_spend.import_modal.summary_spends_updated')}:{' '}
                    <strong className="tabular-nums">{s.spends_updated}</strong>
                  </span>
                  <span>
                    {t('ad_spend.import_modal.summary_spends_unchanged')}:{' '}
                    <strong className="tabular-nums">{s.spends_unchanged}</strong>
                  </span>
                </div>
              </div>

              {campaignsCreated.length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-foreground">
                    {t('ad_spend.import_modal.created_campaigns_title')}
                  </h4>
                  <div className="max-h-[min(280px,40vh)] overflow-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground">ID</TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.col_external_id')}</TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.col_name')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignsCreated.map((c) => (
                          <TableRow key={c.id} className="border-border">
                            <TableCell className="tabular-nums text-foreground">{c.id}</TableCell>
                            <TableCell className="font-mono text-sm text-foreground">{c.external_id}</TableCell>
                            <TableCell className="max-w-[320px] truncate text-sm text-foreground">{c.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {spendsCreated.length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-foreground">
                    {t('ad_spend.import_modal.created_spends_title')}
                  </h4>
                  <div className="max-h-[min(360px,50vh)] overflow-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground">
                            {t('ad_spend.import_modal.col_ad_campaign_id')}
                          </TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.col_external_id')}</TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.import_modal.col_date')}</TableHead>
                          <TableHead className="text-right text-muted-foreground">{t('ad_spend.import_modal.col_amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {spendsCreated.map((row, idx) => (
                          <TableRow key={`${row.ad_campaign_id}-${row.date}-${idx}`} className="border-border">
                            <TableCell className="tabular-nums text-foreground">{row.ad_campaign_id}</TableCell>
                            <TableCell className="font-mono text-sm text-foreground">{row.external_id}</TableCell>
                            <TableCell className="tabular-nums text-foreground">{row.date}</TableCell>
                            <TableCell className="text-right tabular-nums text-foreground">
                              {formatProfitabilityAmount(row.amount, locale, currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {spendsUpdated.length > 0 && (
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-foreground">
                    {t('ad_spend.import_modal.updated_spends_title')}
                  </h4>
                  <div className="max-h-[min(240px,40vh)] overflow-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground">
                            {t('ad_spend.import_modal.col_ad_campaign_id')}
                          </TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.col_external_id')}</TableHead>
                          <TableHead className="text-muted-foreground">{t('ad_spend.import_modal.col_date')}</TableHead>
                          <TableHead className="text-right text-muted-foreground">{t('ad_spend.import_modal.col_amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {spendsUpdated.map((row, idx) => (
                          <TableRow key={`upd-${row.ad_campaign_id}-${row.date}-${idx}`} className="border-border">
                            <TableCell className="tabular-nums text-foreground">{row.ad_campaign_id}</TableCell>
                            <TableCell className="font-mono text-sm text-foreground">{row.external_id}</TableCell>
                            <TableCell className="tabular-nums text-foreground">{row.date}</TableCell>
                            <TableCell className="text-right tabular-nums text-foreground">
                              {formatProfitabilityAmount(row.amount, locale, currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {errList.length > 0 && (
                <Alert variant="destructive" className="border-destructive/50">
                  <AlertTitle>{t('ad_spend.import_modal.errors_title')}</AlertTitle>
                  <AlertDescription>
                    <ul className="list-inside list-disc text-sm">
                      {errList.map((e, i) => (
                        <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end">
                <Button type="button" onClick={handleClose}>
                  {t('ad_spend.import_modal.close')}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalCenter>
    );
  },
);

AdSpendImportModal.displayName = 'AdSpendImportModal';
